import { Component, effect, input, output, signal } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'

import { FilterItemsLoading } from '../../../../components/filter-items-loading'
import { FilterItemsNoResult } from '../../../../components/filter-items-no-result'
import { FilterItemsSearchBar } from '../../../../components/filter-items-search-bar'
import { FilterStringsCheckBox } from '../../../../components/filter-strings-check-box'
import type { stringMap } from '../../../../types'
import { itemDialogOption } from '../../../../types'
import { filterAccordion } from '../../../filter-accordion'

@Component({
  selector: 'dhp-filter-strings-select-accordion',
  templateUrl: './filter-strings-select-accordion.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    filterAccordion,
    FilterItemsSearchBar,
    FilterItemsLoading,
    FilterItemsNoResult,
    FilterStringsCheckBox,
  ],
})
export class FilterStringsSelectAccordion {
  //region Inputs
  // Name of the filter
  public readonly filterName = input.required<string>()
  // The strings that can be filtered on
  public readonly originalStrings = input.required<Set<stringMap>>()
  // The options for extra functionality on the item dialog.
  public readonly itemDialogOptions = input<Set<itemDialogOption>>()
  // The currently selected strings, so the component stays in sync when the selection is cleared or changed by the parent.
  public readonly selected = input<Set<stringMap> | null>(null)
  //endregion Inputs

  //region Outputs
  // The filter output, which is the set of selected strings. It is emitted when the selection changes.
  public readonly stringsOutput = output<Set<stringMap> | null>()
  //endregion Outputs

  //region Properties
  // State of items after filtering with the search term. Initially is empty
  protected readonly strings = signal<Set<stringMap>>(new Set())
  // The set of selected strings. It is used to determine which items are selected and to emit the selected items when they change.
  protected readonly selectedStrings = signal<Set<stringMap>>(new Set())
  //endregion Properties

  constructor() {
    effect(() => {
      // Clone into a new Set so internal filtering doesn't mutate the input set reference
      this.strings.set(new Set(this.originalStrings()))
    })

    effect(() => {
      // Mirror the controlled selection input, matched by key against the current strings.
      const selected = this.selected()
      if (!selected || selected.size === 0) {
        this.selectedStrings.set(new Set())
        return
      }

      const selectedKeys = new Set(Array.from(selected, item => item.key))
      this.selectedStrings.set(
        new Set(
          Array.from(this.strings()).filter(item => selectedKeys.has(item.key))
        )
      )
    })
  }

  //region Methods
  // Checks if the items are available.
  isLoading(): boolean {
    return this.strings().size === 0
  }

  // Checks if an item has been selected based on its id.
  isSelected(selectedString: stringMap): boolean {
    return this.selectedStrings().has(selectedString)
  }

  // Clears the selection of selected items.
  onClearSelection(): void {
    this.selectedStrings.set(new Set())
    this.stringsOutput.emit(null)
  }

  // Modifies the state of the selected items when an item is selected or deselected.
  onItemSelectedChange(selectedString: stringMap): void {
    const updated = new Set(this.selectedStrings())

    this.strings().forEach(baseString => {
      if (baseString === selectedString) {
        if (updated.has(baseString)) {
          updated.delete(baseString)
        } else {
          updated.add(baseString)
        }
      }
    })

    this.selectedStrings.set(updated)

    if (updated.size !== 0) this.stringsOutput.emit(updated)
    else this.stringsOutput.emit(null)
  }

  //region SEARCH functionality
  isSearchOptionEnabled(): boolean {
    const options = this.itemDialogOptions()
    if (!options || options.size === 0) return false

    for (const option of options)
      if (option === itemDialogOption.SEARCH) return true

    return false
  }

  // Filter the visibility of the items shown in the dialog based on search term.
  onSearchTermChange(searchTerm: string): void {
    // If search term is empty reset the items to the original items.
    if (searchTerm === '') {
      this.strings.set(this.originalStrings())
      return
    }

    // Use a new Set to avoid mutating the original items set reference, which is used for resetting the items when the search term is cleared.
    const filtered = new Set<stringMap>()

    // Build items based on search term matching on the original item name's.
    const lowerCaseSearchTerm = searchTerm.toLowerCase()
    for (const originalString of this.originalStrings()) {
      const lowerCaseOriginalString = originalString.title.toLowerCase()

      // Add the original item to the items set when the search term matches with the name.
      if (lowerCaseOriginalString.includes(lowerCaseSearchTerm))
        filtered.add(originalString)
    }

    this.strings.set(filtered)
  }
  //endregion SEARCH functionality
  //endregion Methods
}
